import { getStringWithoutSlash } from '@/lib/utils'

export type TypeMercariProductStatus = 'on_sale' | 'trading' | 'sold_out' | null

export interface IMercariProductListItem {
	currency: string | null
	discountPercentage: number | string | null
	hasPromotions: boolean
	id: string
	images: string[]
	originalPrice: number | null
	name: string
	price: number | null
	status: TypeMercariProductStatus
	url: string
	localPrice?: string // buyaholic case
	currencySymbol?: string // buyaholic case
	warehouse?: string // buyaholic case
	isBuyaholicProduct?: boolean // buyaholic case
	can_express_checkout?: boolean // buyaholic express checkout flag
}

export interface IMercariProductList {
	products: IMercariProductListItem[]
	page: number
	totalCount: number
	hasNext: boolean
}

export interface IMercariProductDetail {
	productInfo: {
		brand: string | null
		category: string | null
		categoryId: number | null
		color: string | null
		currency: string | null
		description: string
		discountTotalReturnAbsolute: number | null
		discountTotalReturnPercent: number | null
		id: string
		images: string[]
		itemCondition: string | null
		itemDiscountReturnAbsolute: number | null
		maxPurchaseQuantity: number | null
		originalPrice: number | null
		name: string
		price: number | null
		shippingDurationMaxDay: number | null
		shippingDurationMinDay: number | null
		shippingPayer: string | null
		size: string | null
		status: TypeMercariProductStatus
		url: string
	}
	sellerInfo: {
		avatar: string | null
		id: string
		infoUrl: string
		name: string
		shop_id: string | null
		ratingNumber: number | null
		starRatingScore: number | null
		verified: boolean | null
	} | null
	localization: {
		category: string | null
		description: string | null
		itemCondition: string | null
		name: string | null
		shippingPayer: string | null
	} | null
}

export interface IMercariProductListApiResponse {
	products: {
		currency: string | null
		discount_percentage: number | null
		has_promotions: boolean | null
		id: string
		images: string[]
		original_price: number | null
		name: string
		price: number | null
		status: TypeMercariProductStatus
		url: string
	}[]
	page: number
	total_count: number
	has_next: boolean
	search_keyword?: string
}

export interface IMercariProductDetailApiResponse {
	product_info: {
		brand: string | null
		category: string | null
		category_id: number | null
		color: string | null
		currency: string | null
		description: string
		discount_total_return_absolute: number | null
		discount_total_return_percent: number | null
		id: string
		images: string[]
		item_condition: string | null
		item_discount_return_absolute: number | null
		max_purchase_quantity: number | null
		original_price: number | null
		name: string
		price: number | null
		shipping_duration_max_day: number | null
		shipping_duration_min_day: number | null
		shipping_payer: string | null
		size: string | null
		status: TypeMercariProductStatus
		url: string
	}
	seller_info: {
		avatar: string | null
		id: string
		info_url: string
		name: string
		shop_id: string | null
		rating_number: number | null
		star_rating_score: number | null
		verified: boolean | null
	}
	localization: {
		category: string | null
		description: string | null
		item_condition: string | null
		name: string | null
		shipping_payer: string | null
	} | null
}

export class ContentApiMercariProductList {
	products: IMercariProductListItem[]

	page: number

	totalCount: number

	hasNext: boolean

	searchKeyword?: string

	constructor(payload: IMercariProductListApiResponse) {
		this.products = payload.products.map(product => {
			return {
				currency: product.currency,
				discountPercentage: product.discount_percentage,
				hasPromotions: Boolean(product.has_promotions),
				id: product.id,
				images: product.images,
				originalPrice: product.original_price,
				name: product.name,
				price: product.price,
				status: product.status,
				url: product.url,
				localPrice: undefined,
				currencySymbol: undefined,
				warehouse: undefined,
				isBuyaholicProduct: false,
				can_express_checkout: false,
			}
		})
		this.page = Number(payload.page)
		this.totalCount = payload.total_count
		this.hasNext = payload.has_next
		this.searchKeyword = payload.search_keyword
	}

	public toModel() {
		return this
	}
}

export class ContentApiMercariProductDetail {
	productInfo: IMercariProductDetail['productInfo']

	sellerInfo: IMercariProductDetail['sellerInfo']

	localization: IMercariProductDetail['localization']

	constructor(payload: IMercariProductDetailApiResponse) {
		this.productInfo = {
			brand: payload.product_info.brand,
			category: payload.product_info.category,
			categoryId: payload.product_info.category_id,
			color: payload.product_info.color,
			currency: payload.product_info.currency,
			description: payload.product_info.description,
			discountTotalReturnAbsolute:
				payload.product_info.discount_total_return_absolute,
			discountTotalReturnPercent:
				payload.product_info.discount_total_return_percent,
			id: payload.product_info.id,
			images: payload.product_info.images,
			itemCondition: payload.product_info.item_condition,
			itemDiscountReturnAbsolute:
				payload.product_info.item_discount_return_absolute,
			maxPurchaseQuantity: payload.product_info.max_purchase_quantity,
			originalPrice: payload.product_info.original_price,
			name: payload.product_info.name,
			price: payload.product_info.price,
			shippingDurationMaxDay: payload.product_info.shipping_duration_max_day,
			shippingDurationMinDay: payload.product_info.shipping_duration_min_day,
			shippingPayer: payload.product_info.shipping_payer,
			size: payload.product_info.size,
			status: payload.product_info.status,
			url: payload.product_info.url,
		}

		this.sellerInfo = payload.seller_info
			? {
					id: payload.seller_info.id,
					shop_id: payload.seller_info.shop_id,
					name: payload.seller_info.name,
					avatar: payload.seller_info.avatar,
					ratingNumber: payload.seller_info.rating_number,
					starRatingScore: payload.seller_info.star_rating_score,
					verified: Boolean(payload.seller_info.verified),
					infoUrl: payload.seller_info.info_url,
			  }
			: null

		this.localization = payload.localization
			? {
					category: payload.localization.category,
					description: payload.localization.description,
					itemCondition: payload.localization.item_condition,
					name: payload.localization.name,
					shippingPayer: payload.localization.shipping_payer,
			  }
			: null
	}

	public toModel() {
		return this
	}
}

export enum MercariProductTypeEnum {
	NORMAL = 'normal',
	SOLD_OUT = 'sold_out',
	DISCOUNT = 'discount',
	SALE = 'sale',
	TRADING = 'trading',
	DISCOUNT_NOT_AVAILABLE = 'discount_not_available',
	SAVED_PERCENTAGE = 'saved_percentage',
}

export const SectionType = {
	COUPON: 'coupon',
	IMAGE: 'image',
	MERCARI_PRODUCT_LIST: 'mercari_product_list',
	BLOG: 'blog',
	TEXT: 'text',
} as const

export type SectionTypeValue = (typeof SectionType)[keyof typeof SectionType]

export const CouponType = {
	BNS_SHOP: 'bnshop',
	SHIPMENT: 'shipment',
	B4U: 'b4u',
} as const

type CouponTypeValue = (typeof CouponType)[keyof typeof CouponType]

export interface Coupon {
	type: CouponTypeValue
	code: string
}

export interface Blog {
	title: string
	url: string
	webImage: string
	appImage: string
	tracking: string
}

export interface Section {
	menuTitle: string
	title: string
	subtitle: string
	type: SectionTypeValue
	coupons?: Coupon[]
	webImage?: string
	url?: string
	tracking?: string
	keywords?: string[]
	blogs?: Blog[]
	content?: string
	appImage?: string
}

export interface Campaign {
	name: string
	shareUrl: string
	appImage: string
	webImage: string
	sections: Section[]
}

export interface CampaignItem {
	slug: string
	imageUrl: string
	enabled: boolean
}

export interface BlogPostCouponCentre {
	imageUrl: string
	enabled: boolean
}

export interface ICampaignHomepageApiResponse {
	is_blog_post_enabled: boolean
	is_promotional_banner_enabled: boolean
	is_notice_enabled: boolean
	is_shopping_tips_enabled: boolean
	express_checkout_view_more_destination: string
	blog_post_coupon_centre: { image_url: string; enabled: boolean }
	campaigns: { slug: string; image_url: string; enabled: boolean }[]
}

export class ContentApiMercariCampaignAppHomepage {
	campaigns: CampaignItem[]

	isBlogPostEnabled: boolean

	isPromotionalBannerEnabled: boolean

	isNoticeEnabled: boolean

	isShoppingTipsEnabled: boolean

	expressCheckoutViewMoreDestination: string

	blogPostCouponCentre: BlogPostCouponCentre

	constructor(payload: ICampaignHomepageApiResponse) {
		this.campaigns = payload.campaigns.map(campaign => {
			return {
				slug: campaign.slug,
				imageUrl: campaign.image_url,
				enabled: campaign.enabled,
			}
		})
		this.isBlogPostEnabled = payload.is_blog_post_enabled
		this.isPromotionalBannerEnabled = payload.is_promotional_banner_enabled
		this.isNoticeEnabled = payload.is_notice_enabled
		this.isShoppingTipsEnabled = payload.is_shopping_tips_enabled
		this.expressCheckoutViewMoreDestination =
			payload.express_checkout_view_more_destination
		this.blogPostCouponCentre = {
			imageUrl: payload.blog_post_coupon_centre.image_url,
			enabled: payload.blog_post_coupon_centre.enabled,
		}
	}

	public toModel() {
		return this
	}
}

export interface IMercariCampaignSlugApiResponse {
	name: string
	share_url: string
	app_image: string
	web_image: string
	meta_description: string
	sections: [
		{
			menu_title: string
			title: string
			subtitle: string
			type: SectionTypeValue
			coupons?: {
				type: CouponTypeValue
				code: string
			}[]
			tabs?: {
				type: string
				shop_id: string
				label: string
				keyword: string
				warehouses?: string
				interests?: string
				sort?: string
				username?: string
			}[]
			web_image?: string
			url?: string
			tracking?: string
			keywords?: string[]
			blogs?: {
				title: string
				url: string
				web_image: string
				app_image: string
				tracking: string
			}[]
			content?: string
			app_image?: string
		},
	]
}

export class ContentApiMercariCampaignSlug {
	sections: Section[]

	name: string

	shareUrl: string

	appImage: string

	webImage: string

	metaDescription: string

	constructor(payload: IMercariCampaignSlugApiResponse) {
		this.sections = payload.sections.map(section => {
			return {
				menuTitle: section.menu_title,
				title: section.title,
				subtitle: section.subtitle,
				type: section.type,
				coupons:
					section.coupons?.map(coupon => {
						return {
							type: coupon.type,
							code: coupon.code,
						}
					}) || [],
				tabs:
					section.tabs?.map(tab => {
						return {
							type: tab.type,
							shop_id: tab.shop_id,
							label: tab.label,
							keyword: tab.keyword,
							warehouses: tab.warehouses,
							interests: tab.interests,
							sort: tab.sort,
							username: tab.username,
						}
					}) || [],
				webImage: section.web_image,
				appImage: section.app_image,
				url: section.url,
				tracking: section.tracking,
				keywords: section.keywords?.map(keyword => keyword),
				blogs: section.blogs?.map(blog => {
					return {
						title: blog.title,
						url: blog.url,
						webImage: blog.web_image,
						appImage: blog.app_image,
						tracking: blog.tracking,
					}
				}),
				content: section.content,
			}
		})
		this.name = payload.name
		this.shareUrl = payload.share_url
		this.appImage = payload.app_image
		this.webImage = payload.web_image
		this.metaDescription = payload.meta_description
	}

	public toModel() {
		return this
	}
}

export interface IMercariLandingBanner {
	banners: {
		image: {
			desktop: string
			mobile: string
		}
		link: string
		title: string
	}[]
}

export class ContentApiMercariLandingBanners {
	banners: IMercariLandingBanner['banners']

	constructor(payload: IMercariLandingBanner) {
		this.banners = payload.banners
	}

	public toModel() {
		return this
	}
}

export enum MercariSearchMode {
	SHOP = 'shop',
	KEYWORD = 'keyword',
	CATEGORY = 'category',
}

interface BaseSearchParams {
	mode: MercariSearchMode
}

export interface ShopModeParams extends BaseSearchParams {
	mode: MercariSearchMode.SHOP
	shop_id: string
	shop_name?: string
	keyword?: never
}

export interface KeywordModeParams extends BaseSearchParams {
	mode: MercariSearchMode.KEYWORD
	keyword: string
	shop_id?: never
	shop_name?: never
}

export type MercariSearchParams =
	| ShopModeParams
	| KeywordModeParams
	| CategoryModeParams

export interface CategoryModeParams extends BaseSearchParams {
	mode: MercariSearchMode.CATEGORY
	category_id: string
	keyword?: never
	shop_id?: never
	shop_name?: never
}
export function isShopModeParams(
	params: MercariSearchParams,
): params is ShopModeParams {
	return params.mode === MercariSearchMode.SHOP
}

export function isKeywordModeParams(
	params: MercariSearchParams,
): params is KeywordModeParams {
	return params.mode === MercariSearchMode.KEYWORD
}

export function isCategoryModeParams(
	params: MercariSearchParams,
): params is CategoryModeParams {
	return params.mode === MercariSearchMode.CATEGORY
}

export function buildSearchParamsFromUrl(
	urlParams: Record<string, string>,
): MercariSearchParams {
	const {
		q,
		shop_id: shopId,
		shop_name: shopName,
		category_id: categoryId,
	} = urlParams

	if (shopId && shopId.trim() !== '') {
		return {
			mode: MercariSearchMode.SHOP,
			shop_id: shopId,
			shop_name: shopName || '',
		}
	}

	if (q && q.trim() !== '') {
		return {
			mode: MercariSearchMode.KEYWORD,
			keyword: q.trim(),
		}
	}

	if (categoryId && categoryId.trim() !== '') {
		return {
			mode: MercariSearchMode.CATEGORY,
			category_id: getStringWithoutSlash(categoryId),
		}
	}

	return {
		mode: MercariSearchMode.KEYWORD,
		keyword: '',
	}
}

export function getDisplayValue(params: MercariSearchParams): string {
	if (isShopModeParams(params)) {
		return params.shop_name || `Shop: ${params.shop_id}`
	}

	if (isCategoryModeParams(params)) {
		return ''
	}

	return params.keyword || ''
}

export const isMercariShopMode = (shop_id?: string): boolean => {
	return !!shop_id && shop_id.trim() !== ''
}

export const getMercariTrackingKeyword = (
	shop_id?: string,
	keyword?: string,
): string => {
	if (isMercariShopMode(shop_id)) {
		return `shop_${shop_id}`
	}
	return keyword || ''
}

export function formatTrackingKeyword(
	isShopModeOrParams: boolean | MercariSearchParams,
	shopId?: string,
	keyword?: string,
): string {
	// only in mercari index page case, because the keyword & shopId is empty
	if (shopId?.trim() === '' && keyword?.trim() === '') {
		return ''
	}

	if (typeof isShopModeOrParams !== 'boolean') {
		const params = isShopModeOrParams

		if (isShopModeParams(params)) {
			return `shop_${params.shop_id}`
		}
		if (isCategoryModeParams(params)) {
			return `category_${params.category_id}`
		}
		return `keyword_${params.keyword || ''}`
	}

	if (isShopModeOrParams && shopId) {
		return `shop_${shopId}`
	}
	return `keyword_${keyword || ''}`
}

export const buildMercariRedirectParams = (
	shop_id?: string,
	keyword?: string,
	shop_name?: string,
) => {
	if (isMercariShopMode(shop_id)) {
		return {
			shop_id,
			shop_name: shop_name || keyword,
		}
	}
	return { searchQuery: keyword }
}

export function buildNavigationParams(
	params: MercariSearchParams,
): Record<string, string> {
	if (isShopModeParams(params)) {
		const result: Record<string, string> = {
			shop_id: params.shop_id,
		}

		if (params.shop_name) {
			result.shop_name = params.shop_name
		}

		return result
	}

	if (isCategoryModeParams(params)) {
		return {
			category_id: params.category_id,
		}
	}

	return {
		q: params.keyword || '',
	}
}
